import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../../lib/api";
import { Modal } from "../../shared/Modal";
import { SearchSelect } from "../../waba/MetricsTab";
import { toastError } from "../../shared/toast";
import { FILE_ACCEPT, errMsg, fileProblem, fmtSize, ghostBtn, inputStyle, labelStyle, primaryBtn, todaySp } from "../common";
import { PIPE_LABEL, uploadKanbanAttachment, useKanbanOptions, type Pipe, type Ref } from "./common";

// Busca de usuários (mesmo endpoint dos participantes de agente). Chips + input.
export function UserPicker({ value, onChange, exclude = [], placeholder = "Buscar usuário pelo nome…" }: {
  value: Ref[]; onChange: (v: Ref[]) => void; exclude?: string[]; placeholder?: string;
}) {
  const [q, setQ] = useState("");
  const { data: found = [] } = useQuery<Ref[]>({
    queryKey: ["participant-search", q],
    queryFn: () => api.get(`/agents/participants/search?q=${encodeURIComponent(q)}`),
    enabled: q.length >= 2,
    staleTime: 10_000,
  });
  const taken = new Set([...value.map(v => v.id), ...exclude]);
  const options = found.filter(u => !taken.has(u.id));
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {value.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {value.map(u => (
            <span key={u.id} style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, padding: "2px 8px", borderRadius: 99, color: "var(--ink)", background: "var(--surface-hover)", border: "1px solid var(--line)" }}>
              {u.name}
              <button type="button" aria-label={`Remover ${u.name}`} onClick={() => onChange(value.filter(x => x.id !== u.id))} style={{ background: "none", border: "none", color: "inherit", cursor: "pointer", padding: 0, fontSize: 13, lineHeight: 1 }}>×</button>
            </span>
          ))}
        </div>
      )}
      <div style={{ position: "relative" }}>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder={placeholder} style={inputStyle} />
        {q.length >= 2 && options.length > 0 && (
          <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 10, background: "var(--surface-card)", border: "1px solid var(--line-bold)", borderRadius: 8, boxShadow: "var(--shadow-pop)", padding: 4, maxHeight: 200, overflowY: "auto" }}>
            {options.map(u => (
              <div key={u.id} onMouseDown={e => { e.preventDefault(); onChange([...value, u]); setQ(""); }} style={{ padding: "7px 10px", borderRadius: 6, fontSize: 12.5, cursor: "pointer", color: "var(--ink)" }}>{u.name}</div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function KanbanCreateModal({ pipe, onClose, onCreated }: { pipe: Pipe; onClose: () => void; onCreated: (id: string) => void }) {
  const qc = useQueryClient();
  const { data: opts } = useKanbanOptions();
  const isTask = pipe === "tasks";
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [tipo, setTipo] = useState("");
  const [subjectId, setSubjectId] = useState("");
  const [agentId, setAgentId] = useState("");
  const [owner, setOwner] = useState<Ref[]>([]);
  const [participants, setParticipants] = useState<Ref[]>([]);
  const [day, setDay] = useState("");
  const [time, setTime] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const subjects = (opts?.subjects ?? []).filter(s => s.tipo === tipo);
  const ownerId = owner[0]?.id ?? opts?.me.id ?? "";
  const ok = !!title.trim() && !busy && (!isTask || (!!tipo && !!subjectId && !!agentId));

  function pick(list: FileList | null) {
    if (!list) return;
    const picked = Array.from(list);
    setErr(picked.map(fileProblem).find(Boolean) ?? null);
    setFiles(prev => [...prev, ...picked.filter(f => !fileProblem(f))]);
    if (fileInput.current) fileInput.current.value = "";
  }

  async function submit() {
    setErr(null);
    setBusy("Criando…");
    let id: string;
    try {
      ({ id } = await api.post<{ id: string }>("/kanban", {
        pipe, title, description,
        tipo: isTask ? tipo : null, subject_id: isTask ? subjectId : null, agent_id: isTask ? agentId : null,
        owner_id: ownerId, participant_ids: participants.map(p => p.id),
        due_date: day || null, due_time: day && time ? time : null,
      }));
    } catch (e) {
      setErr(errMsg(e));
      setBusy(null);
      return;
    }
    const failed: string[] = [];
    for (const [i, f] of files.entries()) {
      setBusy(`Enviando anexo ${i + 1}/${files.length}…`);
      try { await uploadKanbanAttachment(id, f); } catch { failed.push(f.name); }
    }
    qc.invalidateQueries({ queryKey: ["kanban"] });
    if (failed.length) toastError(`Card criado, mas estes anexos falharam: ${failed.join(", ")}`);
    onCreated(id);
  }

  return (
    <Modal onClose={onClose} zIndex={1000}>
      <div style={{ width: 640, maxWidth: "100%", maxHeight: "calc(100vh - 48px)", overflowY: "auto", background: "var(--surface-card)", border: "1px solid var(--line-strong)", borderRadius: 12, boxShadow: "var(--shadow-modal)", padding: 24, display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 600, color: "var(--ink)", flex: 1 }}>Novo card · {PIPE_LABEL[pipe]}</h2>
          <button onClick={onClose} aria-label="Fechar" style={{ background: "none", border: "none", color: "var(--ink-muted)", fontSize: 22, lineHeight: 1, cursor: "pointer" }}>×</button>
        </div>

        {isTask && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <span style={labelStyle}>Tipo</span>
              <select value={tipo} onChange={e => { setTipo(e.target.value); setSubjectId(""); }} style={inputStyle}>
                <option value="">Selecione…</option>
                {(opts?.tipos ?? []).map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <span style={labelStyle}>Assunto</span>
              <select value={subjectId} onChange={e => setSubjectId(e.target.value)} disabled={!tipo} style={inputStyle}>
                <option value="">{tipo ? (subjects.length ? "Selecione…" : "Nenhum assunto cadastrado (Dev › Kanban)") : "Escolha o tipo primeiro"}</option>
                {subjects.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <span style={labelStyle}>Agente</span>
              <SearchSelect value={agentId} onChange={setAgentId} options={(opts?.agents ?? []).map(a => ({ value: a.id, label: a.name }))} placeholder="Selecione o agente" />
            </div>
          </div>
        )}
        <div>
          <label style={labelStyle} htmlFor="k-title">Título</label>
          <input id="k-title" value={title} maxLength={200} onChange={e => setTitle(e.target.value)} style={inputStyle} placeholder={isTask ? "O que precisa ser feito?" : "Nome do cliente / projeto"} />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div>
            <span style={labelStyle}>Dono</span>
            {opts?.can_pick_owner ? (
              <>
                <UserPicker value={owner} onChange={v => setOwner(v.slice(-1))} placeholder={`Vazio = você (${opts.me.name})`} />
              </>
            ) : (
              <div style={{ ...inputStyle, color: "var(--ink-secondary)" }}>{opts?.me.name ?? "…"} (você)</div>
            )}
          </div>
          <div>
            <span style={labelStyle}>Participantes</span>
            <UserPicker value={participants} onChange={setParticipants} exclude={[ownerId]} />
          </div>
        </div>
        <div>
          <span style={labelStyle}>Prazo (opcional)</span>
          <div style={{ display: "flex", gap: 8 }}>
            <input type="date" value={day} min={todaySp()} onChange={e => setDay(e.target.value)} style={{ ...inputStyle, width: 170 }} />
            <input type="time" value={time} onChange={e => setTime(e.target.value)} disabled={!day} style={{ ...inputStyle, width: 120 }} aria-label="Hora (opcional)" />
            {day && <button onClick={() => { setDay(""); setTime(""); }} style={{ ...ghostBtn, padding: "6px 10px" }}>Sem prazo</button>}
          </div>
        </div>
        <div>
          <label style={labelStyle} htmlFor="k-desc">Descrição (markdown)</label>
          <textarea id="k-desc" rows={5} value={description} onChange={e => setDescription(e.target.value)} style={{ ...inputStyle, resize: "vertical" }} placeholder="Contexto, passos, links…" />
        </div>
        <div>
          <div style={{ display: "flex", alignItems: "center", marginBottom: 6 }}>
            <span style={{ ...labelStyle, marginBottom: 0, flex: 1 }}>Anexos</span>
            <input ref={fileInput} type="file" multiple accept={FILE_ACCEPT} hidden onChange={e => pick(e.target.files)} />
            <button type="button" onClick={() => fileInput.current?.click()} style={{ ...ghostBtn, padding: "5px 12px", fontSize: 12 }}>+ Adicionar</button>
          </div>
          {files.map((f, i) => (
            <div key={`${f.name}-${i}`} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, padding: "4px 0", color: "var(--ink)" }}>
              <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</span>
              <span style={{ color: "var(--ink-muted)" }}>{fmtSize(f.size)}</span>
              <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))} aria-label={`Remover ${f.name}`} style={{ background: "none", border: "none", color: "var(--ink-muted)", cursor: "pointer", fontSize: 15 }}>×</button>
            </div>
          ))}
        </div>

        {err && <span style={{ fontSize: 12, color: "var(--danger)" }}>{err}</span>}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", alignItems: "center" }}>
          {busy && <span style={{ fontSize: 12, color: "var(--ink-muted)", marginRight: "auto" }}>{busy}</span>}
          <button onClick={onClose} style={ghostBtn}>Cancelar</button>
          <button onClick={submit} disabled={!ok} style={{ ...primaryBtn, opacity: ok ? 1 : 0.6 }}>Criar card</button>
        </div>
      </div>
    </Modal>
  );
}
